import React, { useState } from 'react';
import { Target, MessageSquare, Bot, Settings, Zap } from 'lucide-react';
import { Input } from '../ui/Input';
import { TextArea } from '../ui/TextArea';
import { Button } from '../ui/Button';
import { ErrorMessage } from '../ui/ErrorMessage';
import { Card } from '../ui/Card';
import { useDebounce } from '../../hooks/useDebounce';
import { advancedRedTeamPersonas } from '../../data/advancedRedTeamPersonas';
import { redTeamPersonas } from '../../data/redTeamPersonas';
import { obfuscationTechniques } from '../../utils/advancedRedTeamLogic';
import type { AdvancedRedTeamConfig } from '../../utils/advancedRedTeamLogic';

interface AdvancedAssessmentFormProps {
  selectedPersona: string;
  onPersonaChange: (persona: string) => void;
  targetUrl: string;
  onTargetUrlChange: (url: string) => void;
  basePrompt: string;
  onBasePromptChange: (prompt: string) => void;
  targetModel: string;
  onTargetModelChange: (model: string) => void;
  onDeploy: (config?: AdvancedRedTeamConfig) => void;
  loading: boolean;
  error?: string;
}

export const AdvancedAssessmentForm: React.FC<AdvancedAssessmentFormProps> = ({
  selectedPersona,
  onPersonaChange,
  targetUrl,
  onTargetUrlChange,
  basePrompt,
  onBasePromptChange,
  targetModel,
  onTargetModelChange,
  onDeploy,
  loading,
  error
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sophisticationLevel, setSophisticationLevel] = useState<'basic' | 'intermediate' | 'advanced'>('basic');
  const [selectedObfuscation, setSelectedObfuscation] = useState<string[]>([]);
  const [multiTurnEnabled, setMultiTurnEnabled] = useState(false);

  // Debounce prompt changes to improve performance
  const debouncedPrompt = useDebounce(basePrompt, 300);

  const allPersonas = [...redTeamPersonas, ...advancedRedTeamPersonas];
  const selectedPersonaData = allPersonas.find(p => p.id === selectedPersona);

  const handleObfuscationToggle = (technique: string) => {
    setSelectedObfuscation(prev => 
      prev.includes(technique) 
        ? prev.filter(t => t !== technique)
        : [...prev, technique]
    );
  };

  const handleAdvancedDeploy = () => {
    const config: AdvancedRedTeamConfig = {
      sophisticationLevel,
      obfuscationTechniques: selectedObfuscation,
      multiTurnEnabled,
      targetWeaknesses: selectedPersonaData?.targetVulnerabilities || []
    };
    onDeploy(config);
  };

  return (
    <div className="space-y-6">
      <Input
        label="🎯 Target AI Endpoint"
        value={targetUrl}
        onChange={onTargetUrlChange}
        placeholder="https://api.openai.com/v1/chat/completions"
        type="url"
        icon={<Target className="h-4 w-4" />}
      />

      <TextArea
        label="🗣️ Base Prompt"
        value={basePrompt}
        onChange={onBasePromptChange}
        placeholder="Enter your test prompt (max 2000 characters)..."
        rows={3}
        icon={<MessageSquare className="h-4 w-4" />}
        maxLength={2000}
        helpText="This prompt will be enhanced by the selected red team agent"
      />

      <Input
        label="🤖 Target Model"
        value={targetModel}
        onChange={onTargetModelChange}
        placeholder="gpt-3.5-turbo"
        icon={<Bot className="h-4 w-4" />}
      />

      {/* Red Team Persona Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Target className="inline h-4 w-4 mr-1" />
          Red Team Agent
        </label>
        <select
          value={selectedPersona}
          onChange={(e) => onPersonaChange(e.target.value)}
          className="w-full px-4 py-3 bg-white border border-red-500/30 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 text-gray-900"
        >
          <optgroup label="Standard Agents">
            {redTeamPersonas.map((persona) => (
              <option key={persona.id} value={persona.id}>
                {persona.name}
              </option>
            ))}
          </optgroup>
          <optgroup label="Advanced Agents">
            {advancedRedTeamPersonas.map((persona) => (
              <option key={persona.id} value={persona.id}>
                {persona.name}
              </option>
            ))}
          </optgroup>
        </select>
        
        {selectedPersonaData && (
          <div className="mt-3 bg-red-50 border border-red-500/30 rounded-md p-4">
            <p className="text-sm text-red-800 mb-3">{selectedPersonaData.description}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <h5 className="text-xs font-semibold text-red-900 mb-1">Attack Tactics</h5>
                <ul className="text-xs text-red-700 space-y-1">
                  {selectedPersonaData.tactics.slice(0, 3).map((tactic, index) => (
                    <li key={index} className="flex items-center space-x-1">
                      <Zap className="h-3 w-3" />
                      <span>{tactic}</span>
                    </li>
                  ))}
                  {selectedPersonaData.tactics.length > 3 && (
                    <li className="text-xs text-red-600">+{selectedPersonaData.tactics.length - 3} more...</li>
                  )}
                </ul>
              </div>
              <div>
                <h5 className="text-xs font-semibold text-red-900 mb-1">Target Vulnerabilities</h5>
                <ul className="text-xs text-red-700 space-y-1">
                  {selectedPersonaData.targetVulnerabilities.slice(0, 3).map((vuln, index) => (
                    <li key={index} className="flex items-center space-x-1">
                      <Target className="h-3 w-3" />
                      <span>{vuln}</span>
                    </li>
                  ))}
                  {selectedPersonaData.targetVulnerabilities.length > 3 && (
                    <li className="text-xs text-red-600">+{selectedPersonaData.targetVulnerabilities.length - 3} more...</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Advanced Configuration Toggle */}
      <div className="flex items-center justify-between">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          <Settings className="h-4 w-4" />
          <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
        </Button>
      </div>

      {/* Advanced Configuration Panel */}
      {showAdvanced && (
        <Card variant="red">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            Advanced Red Team Configuration
          </h3>
          
          <div className="space-y-6">
            {/* Sophistication Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attack Sophistication Level
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(['basic', 'intermediate', 'advanced'] as const).map((level) => (
                  <button
                    key={level}
                    onClick={() => setSophisticationLevel(level)}
                    className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                      sophisticationLevel === level
                        ? 'border-red-500 bg-red-50 text-red-900'
                        : 'border-red-500/30 hover:border-red-500/50 text-gray-700'
                    }`}
                  >
                    <div className="capitalize font-semibold">{level}</div>
                    <div className="text-xs mt-1">
                      {level === 'basic' && 'Simple attacks'}
                      {level === 'intermediate' && 'Multi-step attacks'}
                      {level === 'advanced' && 'Complex exploits'}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Obfuscation Techniques */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Obfuscation Techniques
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.keys(obfuscationTechniques).map((technique) => (
                  <label key={technique} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedObfuscation.includes(technique)}
                      onChange={() => handleObfuscationToggle(technique)}
                      className="rounded border-red-500/30 text-red-500 focus:ring-red-500"
                    />
                    <span className="text-sm text-gray-700 capitalize">
                      {technique.replace(/([A-Z])/g, ' $1').toLowerCase()}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Multi-turn Conversations */}
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={multiTurnEnabled}
                  onChange={(e) => setMultiTurnEnabled(e.target.checked)}
                  className="rounded border-red-500/30 text-red-500 focus:ring-red-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enable Multi-turn Conversation Attacks
                </span>
              </label>
              <p className="text-xs text-gray-600 mt-1">
                Gradually build context to bypass safety measures over multiple exchanges
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="text-center">
        <Button
          onClick={showAdvanced ? handleAdvancedDeploy : () => onDeploy()}
          loading={loading}
          size="lg"
          className="mx-auto"
        >
          <Target className="h-6 w-6" />
          <span>{loading ? 'Deploying Agent...' : 'Deploy Advanced Agent'}</span>
        </Button>
        <p className="text-sm text-gray-600 mt-3">
          {showAdvanced 
            ? `Advanced ${sophisticationLevel} deployment with ${selectedObfuscation.length} obfuscation techniques`
            : 'Standard red team deployment'
          }
        </p>
      </div>

      {error && <ErrorMessage message={error} />}
    </div>
  );
};